import { FileSpreadsheet, FileText, Gavel, Zap } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { IMAGES } from "@/data/kits";
import { scrollToId } from "@/lib/lenis";

export default function Bento() {
  return (
    <section id="systems" className="relative py-24 lg:py-32 bg-panel/40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          overline="The System // Machined for Zero Friction"
          title="Every form your shop needs. One download."
          sub="Not software. Not a subscription. A complete filing cabinet of enforceable shop documents, engineered to slot straight into your day."
        />

        <div className="mt-14 grid lg:grid-cols-12 gap-5">
          <Reveal className="lg:col-span-8 lg:row-span-2">
            <div
              data-testid="bento-card-main"
              className="relative h-full min-h-[420px] clip-notch border border-slate-400/10 overflow-hidden group"
            >
              <img
                src={IMAGES.toolRack}
                alt="Organized shop tool wall"
                className="absolute inset-0 w-full h-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/20" />
              <div className="relative h-full flex flex-col justify-end p-8 lg:p-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-blaze">14 documents / 3 tiers</p>
                <h3 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-slate-50 max-w-md">
                  Organized like a tool wall. Grab what the job needs.
                </h3>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed max-w-lg">
                  Intake, estimates, warranties, payment plans, collections, profit math — each document
                  is print-ready and editable, built from real shop disputes and wins.
                </p>
                <button
                  data-testid="bento-cta-button"
                  onClick={() => scrollToId("kits")}
                  className="mt-6 self-start inline-flex items-center gap-2 bg-blaze text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-3 clip-notch-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(249,115,22,0.4)]"
                >
                  <Zap className="w-4 h-4" /> See the three kits
                </button>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-4" delay={0.1}>
            <div data-testid="bento-card-instant" className="h-full border border-slate-400/10 bg-panel p-8 transition-colors duration-200 hover:border-blaze/40">
              <Zap className="w-6 h-6 text-blaze" />
              <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight text-slate-50">Instant delivery, zero shipping</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Checkout through Square, and your download links unlock on the confirmation page in
                under a minute. PDFs print straight from your phone in the service van.
              </p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-4" delay={0.2}>
            <div data-testid="bento-card-legal" className="h-full border border-slate-400/10 bg-panel p-8 transition-colors duration-200 hover:border-blaze/40">
              <Gavel className="w-6 h-6 text-blaze" />
              <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight text-slate-50">Written for the dispute you'd actually have</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Lien rights, storage fees, teardown liability, warranty carve-outs — the clauses that
                decide who pays when a job goes sideways.
              </p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-4" delay={0.25}>
            <div data-testid="bento-card-universal" className="h-full border border-slate-400/10 bg-panel p-8 transition-colors duration-200 hover:border-blaze/40">
              <div className="flex gap-3">
                <FileText className="w-6 h-6 text-blaze" />
                <FileSpreadsheet className="w-6 h-6 text-blaze" />
              </div>
              <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight text-slate-50">Universal formats</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Print-ready PDF for the clipboard, an Excel calculator with live formulas for the
                office. No logins, no apps, no lock-in.
              </p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-8" delay={0.3}>
            <div className="h-full border border-slate-400/10 bg-raised p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-blaze">The math is the pitch</p>
                <h3 className="mt-2 font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-50">
                  One prevented dispute pays for the Complete System 10x over
                </h3>
              </div>
              <button
                data-testid="bento-calc-button"
                onClick={() => scrollToId("calculator")}
                className="shrink-0 inline-flex items-center gap-2 border border-blaze/50 text-blaze font-display font-bold uppercase tracking-wide text-sm px-6 py-3 transition-colors duration-200 hover:bg-blaze hover:text-white"
              >
                Run your numbers
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
